import React from 'react';
import { Container, Typography, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const MotionCard = motion(Card);
const MotionTypography = motion(Typography);

const services = [
  {
    title: 'Custom Software Development',
    description: 'Tailored software solutions designed to meet your specific business requirements and challenges.',
    image: 'https://source.unsplash.com/random/800x600?software',
  },
  {
    title: 'Mobile App Development',
    description: 'Native and cross-platform mobile applications for iOS and Android with modern UI/UX design.',
    image: 'https://source.unsplash.com/random/800x600?mobile',
  },
  {
    title: 'Web Development',
    description: 'Responsive and scalable web applications built with modern technologies and best practices.',
    image: 'https://source.unsplash.com/random/800x600?web',
  },
  {
    title: 'UI/UX Design',
    description: 'User-centered design solutions that enhance user experience and drive engagement.',
    image: 'https://source.unsplash.com/random/800x600?design',
  },
  {
    title: 'Cloud Solutions',
    description: 'Scalable cloud infrastructure and deployment solutions for your applications.',
    image: 'https://source.unsplash.com/random/800x600?cloud',
  },
  {
    title: 'Maintenance & Support',
    description: 'Ongoing maintenance and technical support to ensure your applications run smoothly.',
    image: 'https://source.unsplash.com/random/800x600?support',
  },
];

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <Container sx={{ py: 8 }}>
      <MotionTypography
        variant="h2"
        component="h1"
        align="center"
        gutterBottom
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Our Services
      </MotionTypography>
      <MotionTypography
        variant="h5"
        align="center"
        color="textSecondary"
        paragraph
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
      >
        Comprehensive software solutions for your business needs
      </MotionTypography>

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <Grid container spacing={4} sx={{ mt: 4 }}>
          {services.map((service, index) => (
            <Grid item key={index} xs={12} sm={6} md={4}>
              <MotionCard
                variants={cardVariants}
                whileHover={{ 
                  scale: 1.03,
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                }}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={service.image}
                  alt={service.title}
                  sx={{
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h5" component="h2">
                    {service.title}
                  </Typography>
                  <Typography>
                    {service.description}
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Container>
  );
};

export default Services; 